import axios from 'axios';
import { AuthService } from './auth.service';

export const API = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const hasBackend = !!import.meta.env.VITE_BACKEND_ENDPOINT;

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('st-access-token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (config.params && typeof config.params === 'object') {
      Object.keys(config.params).forEach((key) => {
        const value = config.params[key];
        if (value === undefined || value === null || value === '') {
          delete config.params[key];
        }
      });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth endpoints that should NOT trigger automatic token refresh
const AUTH_ENDPOINTS_NO_RETRY = [
  '/auth-new/refresh',
  '/auth/token/refresh',
  '/auth/me',
  '/auth/sso/callback',
  '/auth-new/sso/callback',
];

// Prevent concurrent refresh calls - track ongoing refresh promise
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Use responseURL with fallback to config.url for adapters that don't set responseURL
    const responseURL =
      error.response?.request?.responseURL || error.config?.url || '';
    const isAuthEndpoint = AUTH_ENDPOINTS_NO_RETRY.some((endpoint) =>
      responseURL.includes(endpoint)
    );

    if (
      [401, 403].includes(error.response?.status) &&
      !isAuthEndpoint &&
      !error.config._retry
    ) {
      error.config._retry = true;

      // If a refresh is already in progress, wait for it instead of starting another
      if (isRefreshing && refreshPromise) {
        try {
          await refreshPromise;
          // Refresh succeeded, retry the original request with new token
          return API(error.config);
        } catch {
          // Refresh failed, reject the original request
          return Promise.reject(error);
        }
      }

      // Start a new refresh
      isRefreshing = true;
      refreshPromise = AuthService.refreshSession()
        .then(() => {
          isRefreshing = false;
          refreshPromise = null;
        })
        .catch((refreshError) => {
          isRefreshing = false;
          refreshPromise = null;
          throw refreshError;
        });

      try {
        await refreshPromise;
        return API(error.config);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);
